/**
 * PORTÉE D'UNE DÉCISION CITÉE — QA 30/08/2026, BUG 2.
 *
 * `verify-references` ne recoupe une décision que sur son EXISTENCE et ses
 * MÉTADONNÉES (numéro de pourvoi, date, formation) : la recherche officielle
 * renvoie un panorama, jamais le sommaire ni le texte intégral. Une réponse
 * peut donc attribuer à un arrêt réel une solution qu'il ne contient pas —
 * l'arrêt Crim. 1er février 2023, n° 22-80.461 (restitution d'objets saisis)
 * présenté comme un arrêt de prescription des infractions dissimulées — et
 * recevoir malgré tout une pastille verte pleine.
 *
 * Une pastille verte sur une portée inventée est plus dangereuse qu'une
 * absence de pastille : ce module qualifie donc ce qui a réellement été
 * recoupé. Module PUR, sans réseau ni modèle.
 */
import type { VerifiedReference } from "@/lib/api/verify-references";
import { significantTerms } from "@/lib/official-value-check";

/**
 * Un libellé officiel de décision est un simple ÉTIQUETAGE de métadonnées
 * (« Cour de cassation, chambre criminelle, 1 février 2023, 22-80.461 »).
 * Un sommaire, lui, décrit la solution : il est plus long et ne se réduit pas
 * à une juridiction suivie d'une date et d'un numéro.
 */
export function hasOfficialSummary(ref: Pick<VerifiedReference, "officialTitle">): boolean {
  const title = (ref.officialTitle ?? "").replace(/\s+/g, " ").trim();
  // 80 caractères : le plus long étiquetage de métadonnées observé sur le
  // corpus d'éval (« Cour de cassation, chambre commerciale financière et
  // économique, 12 septembre 2024, 22-19.876 ») fait 79 caractères. En deçà,
  // le libellé ne peut pas contenir de sommaire.
  if (title.length < 80) return false;
  // Retire l'étiquetage de métadonnées ; ce qui subsiste est du sommaire.
  const stripped = title
    .replace(/(cour de cassation|conseil d['’]état|chambre[^,]*|cour d['’]appel[^,]*)/gi, "")
    .replace(/\d{1,2}(?:er)?\s+\p{L}+\s+\d{4}/giu, "")
    .replace(/n?°?\s*\d{2}[-.]\d{2}[.\-]?\d{0,3}/g, "")
    .replace(/[,;:()\d.\-\s]/g, "");
  // 40 caractères SUBSISTANT après retrait des métadonnées : en dessous, il ne
  // reste qu'un fragment d'index, insuffisant pour comparer une thèse à un
  // sommaire.
  return stripped.length >= 40;
}

/**
 * RELECTURE QA 30/08/2026, réserve 3. Signaler « portée non recoupée » pour
 * TOUTE décision dépourvue de sommaire revenait à poser une pastille ambre
 * permanente (la recherche officielle renvoie un panorama, jamais le
 * sommaire) : sur le corpus d'éval, 100 % des décisions confirmées basculaient
 * en ambre, ce qui rend le signal illisible et masque le cas réellement
 * dangereux. Le signal ambre est donc réservé aux DIVERGENCES CONSTATÉES
 * entre le sommaire officiel rapatrié et la thèse énoncée (≈ 0 à 5 % des
 * décisions du corpus). Sans sommaire, aucune portée n'est affirmée : la
 * pastille reste celle de l'EXISTENCE, qui est bien ce qui a été recoupé.
 */
/** Mention affichée à la place d'une validation pleine. */
export const CASELAW_SCOPE_NOTE = "existence confirmée — portée divergente du sommaire officiel";

/**
 * Divergence MANIFESTE entre le sommaire officiel et la thèse que la réponse
 * attribue à la décision : aucun terme significatif commun. N'est évalué que
 * lorsqu'un sommaire existe réellement (sinon, la portée est simplement « non
 * recoupée » et n'est pas signalée).
 */
export function hasCaselawScopeDivergence(
  ref: Pick<VerifiedReference, "kind" | "status" | "officialTitle" | "number">,
  body: string,
): boolean {
  if (ref.kind !== "caselaw" || !hasOfficialSummary(ref)) return false;
  const sentence = sentenceCiting(body, ref.number);
  if (!sentence) return false;
  const summary = significantTerms(ref.officialTitle ?? "");
  const claim = significantTerms(sentence);
  if (summary.size < 3 || claim.size < 3) return false;
  for (const term of claim) if (summary.has(term)) return false;
  return true;
}

/** Phrase de la réponse citant le numéro de la décision. */
export function sentenceCiting(body: string, number: string): string | undefined {
  const digits = (number || "").replace(/\D/g, "");
  if (digits.length < 6) return undefined;
  for (const sentence of (body || "").split(/(?<=[.!?])\s+|\n+/)) {
    if (sentence.replace(/\D/g, "").includes(digits)) return sentence;
  }
  return undefined;
}

